import {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import serialize from 'form-serialize';
import AnimationUtils from '@livongo/utils/animation';
import DOMUtils from '@livongo/utils/dom';

export default class Form extends Component {
    static propTypes = {
        /** Additional class(es) to add to the component. */
        className: PropTypes.string,
        /** The form elements to display in the form. */
        children: PropTypes.node.isRequired,
        /** If the form is invalid and animateToInvalid() is called, this will be the element to scroll inside of to the first invalid form item. */
        invalidElementToScroll: PropTypes.node,
        /** The offset to add to the invalid element scroll action. Useful if there is fixed content in the parent and an offset needs to be accounted for. */
        invalidElementScrollOffset: PropTypes.number,
        /**
         * The function to execute when the form is submitted.
         *
         * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
         */
        onSubmit: PropTypes.func,
    };
    static defaultProps = {
        invalidElementScrollOffset: 0,
    };

    constructor(props) {
        super(props);

        this._ID = `Form-${uuidV4()}`;
    }

    _onSubmit = evt => {
        evt.preventDefault();

        const {onSubmit} = this.props;
        const form = evt.currentTarget;

        // for browsers that don't run validation checks on submit
        if (form.checkValidity && !form.checkValidity()) {
            return;
        } else {
            if (onSubmit) {
                onSubmit(evt);
            }
        }
    };

    /**
     * Animates the Form component to the first invalid field.
     *
     * @public
     * @returns {void}
     */
    animateToInvalid() {
        if (this._form.checkValidity && !this._form.checkValidity()) {
            const {
                invalidElementToScroll,
                invalidElementScrollOffset,
            } = this.props;

            // setTimeout to ensure elements are rendered
            setTimeout(() => {
                const errorField = DOMUtils.el(
                    `#${this._ID} .FormElement-contains-error`
                );
                const firstInvalid =
                    errorField.length > 0 ? errorField[0] : errorField;
                const yPos =
                    firstInvalid.offsetTop + invalidElementScrollOffset;
                const elToScroll =
                    invalidElementToScroll || document.scrollingElement;

                AnimationUtils.scrollTo(elToScroll, yPos);
            }, 0);
        }
    }

    /**
     * Checks if the form is valid and ready for submission.
     *
     * @public
     * @returns {Boolean} - Whether the form is valid or not.
     */
    isValid() {
        return this._form.checkValidity ? this._form.checkValidity() : true;
    }

    /**
     * Resets the form fields.
     *
     * @public
     * @returns {void}
     */
    reset() {
        this._form.reset();
    }

    /**
     * Provides the form data as an object for submission.
     *
     * @public
     * @param {Boolean} [withEmpty=false] - Whether to serialize the empty fields.
     * @returns {Object} - The form data to submit.
     */
    submit(withEmpty = false) {
        if (!this.isValid()) {
            return null;
        }

        return serialize(this._form, {hash: true, empty: withEmpty});
    }

    get node() {
        return this._form;
    }

    render() {
        const {className, children} = this.props;
        const classes = classNames('Form', className);

        return (
            <form
                ref={node => {
                    this._form = node;
                }}
                className={classes}
                id={this._ID}
                onSubmit={this._onSubmit}
                action=""
            >
                {children}
            </form>
        );
    }
}
